import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { LanguageSelector } from './LanguageSelector';

export const Header = () => {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const navItems = [
    { label: t.nav.inicio, id: 'hero' },
    { label: t.nav.portfolio, id: 'portfolio' },
    { label: t.nav.proyectos, id: 'proyectos' },
    { label: t.nav.proceso, id: 'proceso' },
    { label: t.nav.sobreMi, id: 'sobre-mi' },
    { label: t.nav.dondeEstoy, id: 'donde-estoy' },
    { label: t.nav.contacto, id: 'contacto' }
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#0e0e10]/95 backdrop-blur-md border-b border-neutral-900 shadow-2xl' : 'bg-transparent border-b border-white/5'
      }`}
    >
      <div className="max-w-[88rem] mx-auto px-5 sm:px-8 lg:px-10">
        <div className="flex justify-between items-center h-24">
          {/* Logo / Brand Mark */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="flex items-center"
          >
            <Link
              to="/app"
              aria-label="Acceso privado de Ramiro"
              title="Acceso privado del estudio"
              className="flex items-center space-x-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400 focus-visible:ring-offset-4 focus-visible:ring-offset-[#0B0B0B]"
              data-testid="logo-button"
            >
              <span className="w-10 h-10 rounded-full border border-gold-400/50 flex items-center justify-center text-gold-400 font-serif text-xl italic shadow-[0_0_24px_rgba(223,192,147,0.12)]">
                R
              </span>
              <div className="leading-tight">
                <span className="font-serif tracking-[0.18em] text-sm block text-gold-400 font-semibold uppercase">
                  Ramiro
                </span>
              </div>
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden 2xl:flex items-center gap-6 text-xs tracking-[0.18em] uppercase">
            {navItems.map((item) => (
              <motion.button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                whileHover={{ color: '#dfc093' }}
                className="text-neutral-400 hover:text-gold-400 transition-colors text-[11px] tracking-[0.18em] uppercase font-medium whitespace-nowrap px-1 py-3"
                data-testid={`nav-${item.id}`}
              >
                {item.label}
              </motion.button>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden 2xl:flex items-center gap-5 pl-3">
            <LanguageSelector />
            <motion.button
              onClick={() => scrollToSection('contacto')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="px-5 py-2.5 text-xs uppercase tracking-widest bg-gold-400 text-black font-semibold rounded hover:bg-gold-300 transition-all shadow-lg shadow-gold-400/10"
              data-testid="cta-reservar-desktop"
            >
              {t.nav.reservar}
            </motion.button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="2xl:hidden text-gold-400 p-3"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{
          opacity: mobileMenuOpen ? 1 : 0,
          height: mobileMenuOpen ? 'auto' : 0
        }}
        transition={{ duration: 0.3 }}
        className="2xl:hidden bg-[#0e0e10]/98 border-b border-neutral-900 overflow-hidden"
      >
        <nav className="flex flex-col px-8 py-8 gap-4">
          {navItems.map((item) => (
            <motion.button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              whileTap={{ scale: 0.98 }}
              className="text-neutral-400 hover:text-gold-400 transition-colors text-left py-4 text-xs tracking-[0.2em] uppercase border-b border-neutral-900"
              data-testid={`mobile-nav-${item.id}`}
            >
              {item.label}
            </motion.button>
          ))}
          
          <div className="pt-4 flex flex-col gap-4">
            <div className="flex justify-start">
              <LanguageSelector />
            </div>
            <motion.button
              onClick={() => scrollToSection('contacto')}
              whileTap={{ scale: 0.98 }}
              className="w-full py-3.5 text-xs uppercase tracking-widest bg-gold-400 text-black font-semibold rounded hover:bg-gold-300 transition-all shadow-lg shadow-gold-400/10"
              data-testid="cta-reservar-mobile"
            >
              {t.nav.reservar}
            </motion.button>
          </div>
        </nav>
      </motion.div>
    </motion.header>
  );
};
